import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaBoxOpen,
  FaSearch,
  FaArrowLeft,
  FaFilter,
  FaDownload,
  FaCalendarAlt,
  FaTag,
  FaList,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import {
  DatePicker,
  Select,
  Input,
  Space,
  Button as AntButton,
  Tag,
  Tooltip as AntTooltip,
  Spin,
} from "antd";
import moment from "moment";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState, fYear } from "../../../components/shared/GlobalState";
import AssetInwardView from "./AssetInwardView";
import dayjs from "dayjs";

const AssetInwardHistoryChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Date filter states
  const [dateRange, setDateRange] = useState([null, null]);
  const [inwardTypeFilter, setInwardTypeFilter] = useState(null);
  const [assetNameFilter, setAssetNameFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // For export functionality
  const [exportLoading, setExportLoading] = useState(false);

  const [{ errorLogApi, user }] = useAtom(gbState);
  const [financial_year] = useAtom(fYear);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: "",
      message: "Are you sure to delete this Family ?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(id),
        },
        {
          label: "No",
          //onClick: () => alert('Click No')
        },
      ],
    });
  };

  const deleteRecord = (id) => {
    let userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };
    axios
      .delete(
        `${process.env.REACT_APP_API_URL}/church/inward/delete/${id}`,
        config
      )
      .then((response) => {
        // alert(JSON.stringify(response));
        getDataFromServer();
      })
      .catch((err) => {
        toast.error("Cannot be deleted");
        errorLogApi(err, "AssetListChild", "/asset/delete");
      });
  };

  useEffect(() => {
    getDataFromServer();
  }, [financial_year]);

  const inwardOptions = [
    { value: 1, label: "Purchase" },
    { value: 2, label: "Donation" },
    { value: 3, label: "Transferred" },
  ];

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/church/inward/${user?.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        //console.log(JSON.stringify(response.data.users)); financial_year[0].label
        const oldData = response.data.purchases?.filter(
          (finFilter) =>
            finFilter?.inward_register?.financial_year === financial_year?.label
        );
        // console.log(oldData);
        const updatedData = oldData.map((data) => {
          return {
            //   ...data,
            id: data?._id,
            asset_id: data?.inward_register?.asset_id?._id,
            asset_name: data?.inward_register?.asset_id?.asset_name,
            item_code: data?.inward_register?.asset_id?.item_code,
            inward_date: data?.inward_register?.inward_date,
            asset_tag: data?.inward_register?.asset_tag,
            quantity: data?.inward_register?.quantity,
            inward_type: inwardOptions.find(
              (option) =>
                option.value === parseInt(data?.inward_register?.inward_type)
            )?.label,
            inward_rate: data?.inward_register?.inward_rate,
            inward_description: data?.inward_register?.inward_description,
            inward_status: data?.inward_register?.inward_status,
            comments: data?.inward_register?.comments,
            purchaser_id:
              data?.inward_register?.purchaser_id?.first_name +
              " " +
              data?.inward_register?.purchaser_id?.last_name,
          };
        });
        console.log(updatedData);
        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
      });
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Search term filter
      const matchesSearch = Object.values(item).some((value) => {
        // Check if value is null or undefined before calling toString()
        if (value === null || value === undefined) return false;
        return value
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });

      // Date range filter
      let matchesDateRange = true;
      if (dateRange[0] && dateRange[1] && item.inward_date) {
        const itemDate = moment(item.inward_date);
        const startDate = moment(dateRange[0]);
        const endDate = moment(dateRange[1]);
        matchesDateRange = itemDate.isBetween(startDate, endDate, "day", "[]");
      }

      // Inward type filter
      let matchesInwardType = true;
      if (inwardTypeFilter && item.inward_type) {
        try {
          const itemType = item.inward_type;
          matchesInwardType = itemType === inwardTypeFilter;
        } catch (e) {
          matchesInwardType = false;
        }
      }

      // Asset name filter
      let matchesAssetName = true;
      if (assetNameFilter && item.asset_name) {
        matchesAssetName = item.asset_name
          .toLowerCase()
          .includes(assetNameFilter.toLowerCase());
      }

      return (
        matchesSearch &&
        matchesDateRange &&
        matchesInwardType &&
        matchesAssetName
      );
    });
  }, [data, searchTerm, dateRange, inwardTypeFilter, assetNameFilter]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Clear all filters
  const clearFilters = () => {
    setDateRange([null, null]);
    setInwardTypeFilter(null);
    setAssetNameFilter("");
  };

  // Export functionality
  const handleExport = async () => {
    try {
      setExportLoading(true);

      // Create CSV content
      const headers = [
        "Item Code",
        "Asset Name",
        "Inward Date",
        "Quantity",
        "Inward Type",
        "Inward Rate",
        "Purchaser",
      ];

      let csvContent = headers.join(",") + "\n";

      // Use filtered data for export
      filteredData.forEach((row) => {
        const inwardType = JSON.parse(row.inward_type)?.label || "";
        const inwardRate = row.inward_rate ? `$${row.inward_rate}` : "";
        const rowData = [
          `"${row.item_code || ""}"`,
          `"${row.asset_name || ""}"`,
          `"${row.inward_date ? row.inward_date.slice(0, 10) : ""}"`,
          `"${row.quantity || ""}"`,
          `"${inwardType}"`,
          `"${inwardRate}"`,
          `"${row.purchaser_id || ""}"`,
        ];
        csvContent += rowData.join(",") + "\n";
      });

      // Create download link
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `asset-inward-history-${moment().format("YYYY-MM-DD")}.csv`
      );
      document.body.appendChild(link);

      // Trigger download
      link.click();
      document.body.removeChild(link);

      toast.success("Export completed successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="p-3 w-full">
      <div className="overflow-visible mb-6 bg-white rounded-xl shadow-md">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <div className="flex justify-center items-center mr-4 w-10 h-10 bg-blue-800 rounded-lg">
                <FaList className="text-white" size={20} />
              </div>
              <h1 className="text-xl font-semibold text-gray-800">
                Asset Inward History
              </h1>
            </div>
            <button
              className="flex items-center px-4 py-2 text-white bg-blue-800 rounded-lg transition-colors hover:bg-blue-900"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft className="mr-2" /> Back
            </button>
          </div>
          <div className="mb-5 border-b border-gray-200"></div>
          <div className="flex gap-2 mb-4">
            <button
              className={`px-4 py-2 rounded-lg border flex items-center ${
                showFilters
                  ? "text-blue-800 bg-blue-50 border-blue-800"
                  : "text-blue-800 border-blue-800 hover:bg-blue-50"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="mr-2" />{" "}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
            <button
              className="flex items-center px-4 py-2 text-green-700 rounded-lg border border-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <Spin size="small" className="mr-2" />
              ) : (
                <FaDownload className="mr-2" />
              )}{" "}
              Export
            </button>
          </div>
          <div className="mb-5">
            <div className="flex gap-4 items-center mb-4">
              <div className="relative w-full">
                <FaSearch className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <input
                  className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {showFilters && (
              <div className="p-5 mb-5 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="flex items-center text-sm font-semibold text-gray-600">
                    <FaFilter className="mr-2" /> Advanced Filters
                  </h3>

                  <div className="flex gap-3">
                    <button
                      onClick={clearFilters}
                      className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 text-sm hover:bg-gray-100"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="px-3 py-1.5 rounded-md bg-blue-800 text-white text-sm hover:bg-blue-900"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3">
                  <div>
                    <div className="flex items-center mb-2">
                      <FaCalendarAlt className="mr-2 text-blue-800" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Date Range
                      </span>
                    </div>
                    <DatePicker.RangePicker
                      className="w-full rounded-lg"
                      onChange={(dates) => setDateRange(dates)}
                      value={dateRange}
                      format="YYYY-MM-DD"
                      placeholder={["Start Date", "End Date"]}
                    />
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <FaTag className="mr-2 text-blue-800" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Inward Type
                      </span>
                    </div>
                    <Select
                      allowClear
                      placeholder="Filter by type"
                      className="w-full rounded-lg"
                      onChange={(value) => setInwardTypeFilter(value)}
                      value={inwardTypeFilter}
                      options={[
                        { value: "Purchase", label: "Purchase" },
                        { value: "Donation", label: "Donation" },
                        { value: "Transfer", label: "Transfer" },
                      ]}
                    />
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <FaList className="mr-2 text-blue-800" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Asset Name
                      </span>
                    </div>
                    <Input
                      placeholder="Filter by asset name"
                      value={assetNameFilter}
                      onChange={(e) => setAssetNameFilter(e.target.value)}
                      className="w-full rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {filteredData.length > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {paginatedData.length}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">{filteredData.length}</span>{" "}
                    records
                    {(dateRange[0] || inwardTypeFilter || assetNameFilter) && (
                      <span className="font-medium text-blue-800">
                        {" "}
                        (filtered)
                      </span>
                    )}
                  </p>
                  {filteredData.length > 0 && (
                    <p className="text-sm text-gray-600">
                      Page {page + 1} of{" "}
                      {Math.ceil(filteredData.length / rowsPerPage)}
                    </p>
                  )}
                </div>

                {(dateRange[0] || inwardTypeFilter || assetNameFilter) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {dateRange[0] && dateRange[1] && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                        Date: {dayjs(dateRange[0]).utc().format("MM/DD/YYYY")} -{" "}
                        {dayjs(dateRange[1]).utc().format("MM/DD/YYYY")}
                        <button
                          onClick={() => setDateRange([null, null])}
                          className="ml-1.5 text-blue-800 hover:text-blue-900"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {inwardTypeFilter && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                        Type: {inwardTypeFilter}
                        <button
                          onClick={() => setInwardTypeFilter(null)}
                          className="ml-1.5 text-blue-800 hover:text-blue-900"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {assetNameFilter && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                        Asset: {assetNameFilter}
                        <button
                          onClick={() => setAssetNameFilter("")}
                          className="ml-1.5 text-blue-800 hover:text-blue-900"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="overflow-hidden bg-white rounded-xl shadow-md">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Action
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Item Code
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Asset Name
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Inward Date
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Quantity
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Inward Type
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Inward Rate
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Purchaser
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row) => (
                      <tr
                        key={row.id}
                        className="transition-colors duration-150 hover:bg-blue-50"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              className="p-1.5 bg-blue-50 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                              onClick={() => {
                                toggleModal();
                                setCurrentRecord(row);
                              }}
                              title="View details"
                            >
                              <FaEye size={14} />
                            </button>
                            {/* <button
                          className="p-1.5 bg-blue-50 rounded-md text-blue-800 hover:bg-blue-100 transition-colors"
                          onClick={() =>
                            navigate("/assets/inventory/update", { state: row })
                          }
                          title="Edit"
                        >
                          <FaEdit size={14} />
                        </button> */}
                            <button
                              className="p-1.5 bg-red-50 rounded-md text-red-600 hover:bg-red-100 transition-colors"
                              onClick={() => handleDelete(row.id)}
                              title="Delete"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-mono text-sm font-medium">
                            {row.item_code}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.asset_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {row.inward_date
                              ? dayjs(row.inward_date)
                                  .utc()
                                  .format("MM/DD/YYYY")
                              : "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.quantity}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              row.inward_type === "Purchase"
                                ? "bg-green-50 text-green-700"
                                : row.inward_type === "Donation"
                                ? "bg-blue-50 text-blue-700"
                                : "bg-blue-50 text-blue-700"
                            }`}
                          >
                            {row.inward_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.inward_rate ? `$${row.inward_rate}` : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.purchaser_id}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center">
                        <p className="font-medium text-gray-600">
                          No records found
                        </p>
                        {(dateRange[0] ||
                          inwardTypeFilter ||
                          assetNameFilter ||
                          searchTerm) && (
                          <p className="mt-1 text-sm text-gray-500">
                            Try adjusting your filters
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="flex justify-between items-center mt-6 mb-2">
            <div className="flex gap-3 items-center">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="px-2 py-1 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
              >
                {[5, 10, 25, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((prev) => prev - 1)}
                className={`px-4 py-2 rounded-lg border text-sm min-w-[100px] ${
                  page === 0
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-blue-800 text-blue-800 hover:bg-blue-50"
                }`}
              >
                Previous
              </button>
              <button
                disabled={
                  paginatedData.length < rowsPerPage ||
                  (page + 1) * rowsPerPage >= filteredData.length
                }
                onClick={() => setPage((prev) => prev + 1)}
                className={`px-4 py-2 rounded-lg border text-sm min-w-[100px] ${
                  paginatedData.length < rowsPerPage ||
                  (page + 1) * rowsPerPage >= filteredData.length
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-blue-800 text-blue-800 hover:bg-blue-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          {/* Modal */}
          <ErrorBoundaryRoutes route="/assets/inventory" page="ModalView">
            {showModal && (
              <Modal onClose={toggleModal}>
                <AssetInwardView currentRecord={currentRecord} />
              </Modal>
            )}
          </ErrorBoundaryRoutes>
        </div>
      </div>
    </div>
  );
};

export default AssetInwardHistoryChild;
